import { Ring, clamp } from '../util/math.js';

/**
 * Fixed-timestep accumulator with interpolation alpha.
 *
 * Simulation (movement, ballistics, AI) runs at a fixed 120 Hz so gunplay feel
 * is identical on a 60 Hz laptop and a 240 Hz monitor. Rendering runs once per
 * rAF and interpolates using `alpha`. `dt` on the render pass is the real
 * frame delta and is what camera/FX smoothing should use.
 */
export class Time {
  constructor({ hz = 120, maxSubSteps = 6 } = {}) {
    this.fixedDt = 1 / hz;
    this.maxSubSteps = maxSubSteps;
    this.accumulator = 0;
    this.alpha = 0;

    this.now = 0;          // seconds since start, unscaled
    this.dt = 0;           // last render delta, scaled + clamped
    this.rawDt = 0;        // last render delta, unscaled
    this.elapsed = 0;      // scaled game time
    this.frame = 0;
    this.scale = 1;        // slow-mo / hitstop

    this._last = 0;
    this._started = false;
    this.frameTimes = new Ring(180);
    this._slowUntil = 0;
  }

  /** Brief global slow-motion, e.g. on the final kill of a wave. */
  hitstop(duration = 0.08, scale = 0.25) {
    this.scale = scale;
    this._slowUntil = this.now + duration;
  }

  /**
   * Advance one render frame. Returns the number of fixed steps to run.
   * Caller pattern:
   *   const steps = time.begin(ts);
   *   for (let i = 0; i < steps; i++) world.fixedUpdate(time.fixedDt);
   *   world.update(time.dt, time.alpha);
   */
  begin(timestampMs) {
    const t = timestampMs / 1000;
    if (!this._started) { this._started = true; this._last = t; this.dt = 0; this.rawDt = 0; return 0; }

    let raw = t - this._last;
    this._last = t;
    this.now += raw;

    // A tab regaining focus reports a huge delta; clamping keeps the player
    // from teleporting through a wall on the first frame back.
    raw = clamp(raw, 0, 0.25);
    this.rawDt = raw;
    this.frameTimes.push(raw * 1000);

    if (this._slowUntil && this.now >= this._slowUntil) { this.scale = 1; this._slowUntil = 0; }

    this.dt = raw * this.scale;
    this.elapsed += this.dt;
    this.frame++;

    this.accumulator += this.dt;
    let steps = 0;
    while (this.accumulator >= this.fixedDt && steps < this.maxSubSteps) {
      this.accumulator -= this.fixedDt;
      steps++;
    }
    // If we blew the sub-step budget the machine cannot keep up; drop the
    // backlog instead of entering a death spiral where each frame owes more.
    if (steps >= this.maxSubSteps) this.accumulator = 0;

    this.alpha = this.accumulator / this.fixedDt;
    return steps;
  }

  get fps() { const a = this.frameTimes.avg; return a > 0 ? 1000 / a : 0; }
  get ms99() { return this.frameTimes.percentile(0.99); }
}
